import React, {useEffect, useState} from "react";
import Image1 from "../assets/images/tractor.svg";
import {FormGroup, Label} from "reactstrap";
import {useTranslation} from "react-i18next";
import Select from "react-select";
import {useConfigurator} from "../context/ConfiguratorContext";
import {
    createCustomerInquiry,
    getDealersList,
    getStatesList,
    getDealerId
} from "../services/TractorService";
import Utils from "../utils/index";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import TractorSidebar from "./TractorSidebar";
import {useParams} from "react-router-dom";

const TractorReview = ({setCurrentStep}) => {
    const {dealerUuid} = useParams();
    const [isDealerLocked, setIsDealerLocked] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {
        selectedTractor,
        selectedTires,
        selectedUpgrades,
        selectedAttachments,
        selectedEquipment,
        selectedAccessories,
        setSelectedTractor,
        setSelectedTires,
        setSelectedUpgrades,
        setSelectedAttachments,
        setSelectedEquipment,
        setSelectedAccessories,
    } = useConfigurator();

    const [nextStep, setNextStep] = useState(1);
    const [reviewData, setReviewData] = useState({
        id: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        state_id: "",
        dealer_id: "",
        comment: "",
        city: "",
        zip_code: "",
        latitude: "",
        longitude: "",
        is_whatsapp: 0,
    });
    const [errors, setErrors] = useState({
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        state_id: "",
        dealer_id: "",
        comment: "",
    });
    const [stateList, setStateList] = useState([]);
    const [dealerList, setDealerList] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState(null);

    const validate = () => {
        let newErrors = {};

        if (!reviewData.first_name) {
            newErrors.first_name = "First name is required";
        }
        if (!reviewData.last_name) {
            newErrors.last_name = "Last name is required";
        }

        if (!reviewData.email) {
            newErrors.email = "Email is required";
        } else if (!/^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/.test(reviewData.email)) {
            newErrors.email = "Invalid email format";
        }

        if (reviewData.mobile && !/^\d{10}$/.test(reviewData.mobile)) {
            newErrors.mobile = "Mobile number must be exactly 10 digits";
        }
        if (!reviewData.state_id) {
            newErrors.state_id = "State is required";
        }
        if (!reviewData.city) {
            newErrors.city = "City is required";
        }

        if (!reviewData.zip_code) {
            newErrors.zip_code = "Zipcode is required";
        } else if (!/^\d{5,}$/.test(reviewData.zip_code)) {
            newErrors.zip_code = "Zipcode must be at least 5 digits";
        }

        return newErrors;
    };

    const handleBack = () => {
        setCurrentStep(6);
    };

    const handleNext = () => {
        setNextStep(2);
    };

    const handleSubmitBack = () => {
        setNextStep(1);
    };

    useEffect(() => {
        const data = {country_id: ""};
        getStatesList(data)
            .then((response) => {
                const formatted = response.data.data.map((state) => ({
                    label: state.name,
                    value: state.id,
                }));
                setStateList(formatted);
            })
            .catch((err) => {
            });
    }, []);
    useEffect(() => {
        if (selectedTractor?.uuid && reviewData?.state_id && reviewData?.city && reviewData?.zip_code) {
            const selectedState = stateList.find(
                (state) => state.value === reviewData.state_id
            );

            const data = {
                state_id: reviewData.state_id,
                state_name: selectedState ? selectedState.label : "",
                city: reviewData.city.trim(),
                zip_code: reviewData.zip_code,
                uuid: selectedTractor.uuid,
            };

            getDealersList(data)
                .then((response) => {
                    const formatted = response.data.data.map(dealer => ({
                        label: dealer.distance ? `${dealer.value} (${dealer.distance} Miles)` : dealer.value,
                        value: dealer.id,
                    }));

                    setDealerList(formatted);
                    setSelectedDealer(null);
                })
                .catch((err) => {
                    console.error("Error fetching dealers:", err);
                    setDealerList([]);
                });
        } else {
            setDealerList([]);
        }
    }, [
        reviewData.state_id,
        reviewData.city,
        reviewData.zip_code,
        selectedTractor?.uuid,
        stateList,
    ]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
        } else {
            setErrors("");
            setBtnLoading(true);
            const configurations = [];

            const addToConfigurations = (list, type) => {
                list?.forEach((item) => {
                    configurations.push({
                        type: type,
                        id: item.id,
                        price: item.price || 0,
                        is_included_price: item.is_price_included ?? 0,
                    });
                });
            };

            addToConfigurations(selectedTires, "tires");
            addToConfigurations(selectedUpgrades, "upgrades");
            addToConfigurations(selectedAttachments, "attachments");
            addToConfigurations(selectedEquipment, "equipment");
            addToConfigurations(selectedAccessories, "accessories");

            const total_price =
                (selectedTractor?.price || 0) +
                selectedTires.reduce(
                    (total, item) =>
                        total + (item.is_price_included === 0 ? item.price : 0 || 0),
                    0
                ) +
                selectedUpgrades.reduce(
                    (total, item) =>
                        total + (item.is_price_included === 0 ? item.price : 0 || 0),
                    0
                ) +
                selectedAttachments.reduce(
                    (total, item) =>
                        total + (item.is_price_included === 0 ? item.price : 0 || 0),
                    0
                ) +
                selectedEquipment.reduce(
                    (total, item) =>
                        total + (item.is_price_included === 0 ? item.price : 0 || 0),
                    0
                ) +
                selectedAccessories.reduce(
                    (total, item) =>
                        total + (item.is_price_included === 0 ? item.price : 0 || 0),
                    0
                );

            const data = {
                first_name: reviewData.first_name?.trim(),
                last_name: reviewData.last_name?.trim(),
                mobile: reviewData.mobile,
                email: reviewData.email,
                state_id: reviewData.state_id,
                dealer_id: reviewData?.dealer_id,
                comment: reviewData?.comment,
                tractor_id: selectedTractor?.id,
                configurations: configurations,
                is_whatsapp: reviewData?.is_whatsapp,
                total_price: total_price,
                tractor_price: selectedTractor?.price,
                city: reviewData?.city,
                zip_code: reviewData?.zip_code,
                latitude: reviewData?.latitude,
                longitude: reviewData?.longitude,
                lead_from: dealerUuid ? 2 : 1,
                tax: 0,
            };

            createCustomerInquiry(data)
                .then((response) => {
                    toast.success(response.data.message);
                    setBtnLoading(false);
                    setReviewData({
                        id: "",
                        first_name: "",
                        last_name: "",
                        email: "",
                        mobile: "",
                        state_id: "",
                        dealer_id: "",
                        comment: "",
                    });

                    if (dealerUuid) {
                        navigate(`/dealer/${dealerUuid}/thank-you`, {state: response.data.data});
                    } else {
                        navigate("/thank-you", {state: response.data.data});
                    }
                    setSelectedTractor(null);
                    setSelectedTires([]);
                    setSelectedUpgrades([]);
                    setSelectedAttachments([]);
                    setSelectedEquipment([]);
                    setSelectedAccessories([]);
                })
                .catch((err) => {
                    setBtnLoading(false);

                    toast.error(Utils.getErrorMessage(err));
                });
        }
    };

    useEffect(() => {
        if (dealerUuid) {
            const data = {dealerUuid: dealerUuid};

            getDealerId(data)
                .then((res) => {
                    const id = res.data.data?.id;
                    if (id) {
                        setReviewData((prev) => ({...prev, dealer_id: id}));
                        setIsDealerLocked(true);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch dealer by UUID", err);
                });
        }
    }, [dealerUuid]);
    return (
        <div className="container-fluid">
            {nextStep === 2 && nextStep !== 3 ? (
                <>
                    <div className="row mb-5">
                        <div className="col-lg-9">
                            <div className="card ">
                                <div className="card-body">
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <h4 className="tractor-heading-main  mb-4">
                                                {t("submitYourInformation")}
                                            </h4>
                                            <p className="text-muted text-center mt-2">
                                                Ready to take the next step with your Solis tractor? When you submit
                                                your configuration, you’ll be instantly connected with a nearby dealer
                                                who can confirm pricing, availability, and options tailored to your
                                                needs. Plus, you can see if you qualify for financing, explore current
                                                special offers, and get a personalized quote on your build — all with no
                                                obligation. Hitting “Submit” is the fastest way to turn your dream
                                                tractor into reality!
                                            </p>

                                            <div className="row">
                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("firstName")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                                                <input
                                                                    className="form-control input-search"
                                                                    type="text"
                                                                    value={reviewData.first_name}
                                                                    onChange={(e) => {
                                                                        setReviewData({
                                                                            ...reviewData,
                                                                            first_name: e.target.value,
                                                                        });
                                                                    }}
                                                                    name={`first_name`}
                                                                    maxLength={50}
                                                                    placeholder="First Name"
                                                                />
                                                                {errors.first_name && (
                                                                    <div className={`text-danger mt-1`}>
                                                                        {errors.first_name}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>

                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("lastName")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                                                <input
                                                                    className="form-control input-search"
                                                                    type="text"
                                                                    value={reviewData.last_name}
                                                                    onChange={(e) => {
                                                                        setReviewData({
                                                                            ...reviewData,
                                                                            last_name: e.target.value,
                                                                        });
                                                                    }}
                                                                    name={`last_name`}
                                                                    maxLength={50}
                                                                    placeholder="Last Name"
                                                                />
                                                                {errors.last_name && (
                                                                    <div className={`text-danger mt-1`}>
                                                                        {errors.last_name}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>

                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("email")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                                                <input
                                                                    className="form-control input-search"
                                                                    type="text"
                                                                    value={reviewData.email}
                                                                    onChange={(e) => {
                                                                        setReviewData({
                                                                            ...reviewData,
                                                                            email: e.target.value,
                                                                        });
                                                                    }}
                                                                    name={`email`}
                                                                    maxLength={50}
                                                                    placeholder="Email"
                                                                />
                                                                {errors.email && (
                                                                    <div className={`text-danger mt-1`}>
                                                                        {errors.email}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>

                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("mobile")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                                                <input
                                                                    className="form-control input-search"
                                                                    type="number"
                                                                    value={reviewData.mobile}
                                                                    onChange={(e) => {
                                                                        setReviewData({
                                                                            ...reviewData,
                                                                            mobile: e.target.value,
                                                                        });
                                                                    }}
                                                                    name={`mobile`}
                                                                    maxLength={50}
                                                                    placeholder="Mobile"
                                                                />
                                                                {errors.mobile && (
                                                                    <div className={`text-danger mt-1`}>
                                                                        {errors.mobile}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>
                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("city")}
                                                        </Label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="city"
                                                            name={`city`}
                                                            value={reviewData.city}
                                                            onChange={(e) => {
                                                                setReviewData({
                                                                    ...reviewData,
                                                                    city: e.target.value,
                                                                });
                                                            }}
                                                            placeholder="City"
                                                        />
                                                        {errors.city && (
                                                            <div className={`text-danger mt-1`}>
                                                                {errors.city}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </div>
                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("state")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                                                <Select
                                                                    className="my-select-v"
                                                                    options={stateList}
                                                                    value={stateList.find(
                                                                        (item) => item.value === reviewData.state_id
                                                                    )}
                                                                    onChange={(selectedOption) =>
                                                                        setReviewData({
                                                                            ...reviewData,
                                                                            state_id: selectedOption
                                                                                ? selectedOption.value
                                                                                : "",
                                                                        })
                                                                    }
                                                                    isSearchable={true}
                                                                    placeholder={"Select State"}
                                                                />
                                                                {errors.state_id && (
                                                                    <div className={`text-danger mt-1`}>
                                                                        {errors.state_id}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>
                                                <div className="col-md-6">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("zipcode")}
                                                        </Label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="zip_code"
                                                            name={`zip_code`}
                                                            value={reviewData.zip_code}
                                                            onChange={(e) => {
                                                                setReviewData({
                                                                    ...reviewData,
                                                                    zip_code: e.target.value,
                                                                });
                                                            }}
                                                            placeholder="Zipcode"
                                                        />
                                                        {errors.zip_code && (
                                                            <div className={`text-danger mt-1`}>
                                                                {errors.zip_code}
                                                            </div>
                                                        )}
                                                    </FormGroup>
                                                </div>

                                                {!isDealerLocked ? (
                                                    <div className="col-md-12">
                                                        <FormGroup>
                                                            <Label className="col-form-label ml-2 Label-my">
                                                                {t("dealerNearYou")}
                                                            </Label>
                                                            <div className="col-xl-12">
                                                                <div className="input-div">
                                                                    <Select
                                                                        className="my-select-v"
                                                                        options={dealerList}
                                                                        value={selectedDealer}
                                                                        onChange={(selectedOption) => {
                                                                            setSelectedDealer(selectedOption || null);
                                                                            setReviewData({
                                                                                ...reviewData,
                                                                                dealer_id: selectedOption ? selectedOption.value : "",
                                                                            });
                                                                        }}
                                                                        isSearchable={true}
                                                                        isClearable={true}
                                                                        placeholder={"Select Dealer near you"}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </FormGroup>
                                                    </div>
                                                ) : (
                                                    <input type="hidden" name="dealer_id" value={reviewData.dealer_id}/>
                                                )}

                                                <div className="col-md-12">
                                                    <FormGroup>
                                                        <Label className="col-form-label ml-2 Label-my">
                                                            {t("comment")}
                                                        </Label>
                                                        <div className="col-xl-12">
                                                            <div className="input-div">
                                <textarea
                                    className="form-control input-search"
                                    type="text"
                                    value={reviewData.comment}
                                    onChange={(e) => {
                                        setReviewData({
                                            ...reviewData,
                                            comment: e.target.value,
                                        });
                                    }}
                                    name={`comment`}
                                    maxLength={50}
                                    placeholder="Comment"
                                    rows={3}
                                />
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tractor-scroll-container custom-scrollbar">
                                        <div className="row"></div>
                                        {" "}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-3 mt-4 mt-lg-0">
                            <div className="total-price-box-tires">
                                <div className="total-price-content">
                                    <p className="mb-2 mt-2 total-price-text">
                                        {t("totalPrice")}: ${" "}
                                        {(
                                            (selectedTractor?.price || 0) +
                                            selectedTires.reduce((total, tire) => {
                                                return (
                                                    total +
                                                    (tire.is_price_included === 0 ? tire.price : 0)
                                                );
                                            }, 0) +
                                            selectedUpgrades.reduce((total, tire) => {
                                                return (
                                                    total +
                                                    (tire.is_price_included === 0 ? tire.price : 0)
                                                );
                                            }, 0) +
                                            selectedAttachments.reduce((total, tire) => {
                                                return (
                                                    total +
                                                    (tire.is_price_included === 0 ? tire.price : 0)
                                                );
                                            }, 0) +
                                            selectedEquipment.reduce((total, tire) => {
                                                return (
                                                    total +
                                                    (tire.is_price_included === 0 ? tire.price : 0)
                                                );
                                            }, 0) +
                                            selectedAccessories.reduce((total, tire) => {
                                                return (
                                                    total +
                                                    (tire.is_price_included === 0 ? tire.price : 0)
                                                );
                                            }, 0)
                                        ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                        })}
                                    </p>
                                </div>
                                <div className="border-line-row mb-3"></div>
                                <p className="price-text-tires">{t("tractorModel")}</p>
                                <p className="price-text-tires-description">
                                    {selectedTractor?.short_detail}{" "}
                                </p>
                                <p
                                    className="price-text-tires"
                                    style={{
                                        marginTop: "-21px",
                                    }}
                                >
                                    $ {selectedTractor?.price}
                                </p>

                                {selectedTires?.length > 0 && (
                                    <>
                                        <div className="border-line-row mb-3"></div>
                                        <p className="price-text-tires">{t("tire")}</p>
                                        {selectedTires?.map((item, index) => {
                                            return (
                                                <React.Fragment key={index}>
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: item.detail,
                                                        }}
                                                        className="price-text-tires-description"
                                                    />
                                                    <p
                                                        className="price-text-tires"
                                                        style={{
                                                            marginTop: "-21px",
                                                        }}
                                                    >
                                                        $ {item?.price}
                                                    </p>
                                                </React.Fragment>
                                            );
                                        })}
                                    </>
                                )}

                                {selectedUpgrades?.length > 0 && (
                                    <>
                                        <div className="border-line-row mb-3"></div>
                                        <p className="price-text-tires">{t("upgrades")}</p>
                                        {selectedUpgrades?.map((item, index) => (
                                            <p key={index} className="price-text-tires-description">
                                                {item.name}:{" "}
                                                <span style={{fontWeight: 700}}>$ {item.price}</span>
                                            </p>
                                        ))}
                                    </>
                                )}

                                {selectedAttachments?.length > 0 && (
                                    <>
                                        <hr
                                            style={{
                                                border: "2px solid #d32225",
                                                marginTop: "-12px",
                                            }}
                                        />
                                        <p className="price-text-tires">{t("attachments")}</p>
                                        {selectedAttachments?.map((item, index) => (
                                            <p key={index} className="price-text-tires-description">
                                                {item.name}:{" "}
                                                <span style={{fontWeight: 700}}>$ {item.price}</span>
                                            </p>
                                        ))}
                                    </>
                                )}

                                {selectedEquipment?.length > 0 && (
                                    <>
                                        <hr
                                            style={{
                                                border: "2px solid #d32225",
                                                marginTop: "-12px",
                                            }}
                                        />
                                        <p className="price-text-tires">{t("equipment")}</p>
                                        {selectedEquipment?.map((item, index) => (
                                            <p key={index} className="price-text-tires-description">
                                                {item.name}:{" "}
                                                <span style={{fontWeight: 700}}>$ {item.price}</span>
                                            </p>
                                        ))}
                                    </>
                                )}

                                {selectedAccessories?.length > 0 && (
                                    <>
                                        <hr
                                            style={{
                                                border: "2px solid #d32225",
                                                marginTop: "-12px",
                                            }}
                                        />
                                        <p className="price-text-tires">{t("accessories")}</p>
                                        {selectedAccessories?.map((item, index) => (
                                            <p key={index} className="price-text-tires-description">
                                                {item.name}:{" "}
                                                <span style={{fontWeight: 700}}>$ {item.price}</span>
                                            </p>
                                        ))}
                                    </>
                                )}
                            </div>

                            <div className="d-flex justify-content-between mt-3">
                                <button
                                    className="btn btn-outline-theme tractor-custom-button-details"
                                    onClick={handleSubmitBack}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {t("back")}
                                </button>
                                {btnLoading === true ? (
                                    <button
                                        type="button"
                                        disabled
                                        className="btn btn-danger tractor-custom-button-details tractor-btn-right"
                                    >
                    <span
                        className="spinner-border spinner-border-sm"
                        aria-hidden="true"
                    ></span>{" "}
                                        Loading...
                                    </button>
                                ) : (
                                    <button
                                        className="btn btn-danger tractor-custom-button-details tractor-btn-right"
                                        onClick={handleSubmit}
                                    >
                                        {t("Submit")}
                                        <i className="bi bi-arrow-right"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                nextStep !== 3 && (
                    <>
                        <div className="row">
                            <div className="col-lg-9">
                                <div className="card bg-card">
                                    <div className="card-body">
                                        <div className="row mb-4">
                                            <div className="col-12">
                                                <h4 className="tractor-heading-main mb-4">
                                                    {t("reviewYourPackage")}
                                                </h4>
                                                <p className="text-muted d-flex justify-content-center mt-2">
                                                    {t(
                                                        "reviewYourCompleteTractorConfigurationBeforeSubmittingYourRequest"
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="tractor-scroll-container custom-scrollbar">
                                            <div className="row ">
                                                <div className="col-md-12">
                                                    <div className="card-review mb-4">
                                                        <div className="card-body">
                                                            <h5 className="card-title-review">
                                                                <img
                                                                    src={Image1}
                                                                    alt="tractor"
                                                                    className="me-2"
                                                                />
                                                                {t("selectedTractor")}{" "}
                                                            </h5>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6 className="card-title card-title-custom">
                                                                    {selectedTractor?.model}{" "}
                                                                    {selectedTractor?.name}
                                                                </h6>
                                                                <p className="card-price mb-0">
                                                                    {Utils.priceNumberFormat(
                                                                        selectedTractor?.price
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <div className="row row-cols-2 g-2 small">
                                                                    {selectedTractor?.attributeList?.map(
                                                                        (item, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className={`col d-flex align-items-center gap-2`}
                                                                            >
                                                                                <img
                                                                                    src={item.icon}
                                                                                    alt={item.concat_value}
                                                                                    style={{
                                                                                        width: "23px",
                                                                                        height: "23px",
                                                                                    }}
                                                                                />
                                                                                <span>
                                          {item.attribute_value}{" "}
                                                                                    {item.concat_value}
                                        </span>
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="card-review mb-4">
                                                        <div className="card-body">
                                                            <h5
                                                                className="card-title-review"
                                                                style={{
                                                                    color: "#D32225",
                                                                }}
                                                            >
                                                                {t("packageSummary")}
                                                            </h5>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6 className="card-title-custom">
                                                                    {t("baseTractor")}
                                                                </h6>
                                                                <p className="card-price-review mb-0">
                                                                    {Utils.priceNumberFormat(
                                                                        selectedTractor?.price
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6 className="card-title-custom">
                                                                    {t("optionsEquipment")}
                                                                </h6>
                                                                <p className="card-price-review mb-0">
                                                                    +{" "}
                                                                    {Utils.priceNumberFormat(
                                                                        selectedTires.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedUpgrades.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedAttachments.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedEquipment.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedAccessories.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        )
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6 className="card-title-custom">
                                                                    {t("subtotal")}
                                                                </h6>
                                                                <p className="card-price-review mb-0 text-danger">
                                                                    {Utils.priceNumberFormat(
                                                                        (selectedTractor?.price || 0) +
                                                                        selectedTires.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedUpgrades.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedAttachments.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedEquipment.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedAccessories.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        )
                                                                    )}
                                                                </p>
                                                            </div>
                                                            <hr/>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6 className="card-title-custom">
                                                                    {t("estimatedTax")} (0%)
                                                                </h6>
                                                                <p className="card-price-review mb-0">
                                                                    + $0.00
                                                                </p>
                                                            </div>
                                                            <hr/>
                                                            <div
                                                                className="d-flex justify-content-between align-items-center">
                                                                <h6
                                                                    className="card-title-custom"
                                                                    style={{
                                                                        color: "#d32225",
                                                                    }}
                                                                >
                                                                    {t("total")}
                                                                </h6>
                                                                <p className="card-price mb-0">
                                                                    {Utils.priceNumberFormat(
                                                                        (selectedTractor?.price || 0) +
                                                                        selectedTires.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedUpgrades.reduce((total, tire) => {
                                                                            return (
                                                                                total +
                                                                                (tire.is_price_included === 0
                                                                                    ? tire.price
                                                                                    : 0)
                                                                            );
                                                                        }, 0) +
                                                                        selectedAttachments.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedEquipment.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        ) +
                                                                        selectedAccessories.reduce(
                                                                            (total, tire) => {
                                                                                return (
                                                                                    total +
                                                                                    (tire.is_price_included === 0
                                                                                        ? tire.price
                                                                                        : 0)
                                                                                );
                                                                            },
                                                                            0
                                                                        )
                                                                    )}
                                                                </p>
                                                            </div>

                                                            <h6 className="card-title-custom-review mt-2 d-flex justify-content-center">
                                                                *
                                                                {t(
                                                                    "finalPricingMayVaryByDealerTaxRatesVaryByLocation"
                                                                )}
                                                                .{" "}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-md-12">
                                                    <div className="card-review mb-4">
                                                        <div className="card-body">
                                                            <h5
                                                                className="card-title-review mb-3"
                                                                style={{
                                                                    color: "#D32225",
                                                                }}
                                                            >
                                                                {t("selectedOptionsEquipment")}
                                                            </h5>
                                                            <p className="price-text-tires ps-0">
                                                                {t("tractorModel")}
                                                            </p>
                                                            <p className="price-text-tires-description mb-0 ps-0">
                                                                {selectedTractor?.short_detail}{" "}
                                                            </p>
                                                            <p className="price-text-tires ps-0">
                                                                {Utils.priceNumberFormat(
                                                                    selectedTractor?.price
                                                                )}
                                                            </p>

                                                            {selectedTires?.length > 0 && (
                                                                <>
                                                                    <p className="price-text-tires ps-0">
                                                                        {selectedTires?.length === 1
                                                                            ? t("tire")
                                                                            : t(`tires`)}
                                                                    </p>
                                                                    {selectedTires?.map((item, index) => {
                                                                        return (
                                                                            <p
                                                                                key={index}
                                                                                className="price-text-tires-description ps-0"
                                                                            >
                                                                                ({index + 1}) {item.name}
                                                                            </p>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}

                                                            {selectedUpgrades?.length > 0 && (
                                                                <>
                                                                    <p className="price-text-tires ps-0">
                                                                        {selectedUpgrades.length === 1
                                                                            ? t("upgrade")
                                                                            : t("upgrades")}
                                                                    </p>
                                                                    {selectedUpgrades?.map((item, index) => (
                                                                        <p
                                                                            key={index}
                                                                            className="price-text-tires-description ps-0"
                                                                        >
                                                                            ({index + 1}) {item.name}
                                                                        </p>
                                                                    ))}
                                                                </>
                                                            )}

                                                            {selectedAttachments?.length > 0 && (
                                                                <>
                                                                    <p className="price-text-tires ps-0">
                                                                        {selectedAttachments.length === 1
                                                                            ? t("attachment")
                                                                            : t("attachments")}
                                                                    </p>
                                                                    {selectedAttachments?.map((item, index) => (
                                                                        <p
                                                                            key={index}
                                                                            className="price-text-tires-description ps-0"
                                                                        >
                                                                            ({index + 1}) {item.name}
                                                                        </p>
                                                                    ))}
                                                                </>
                                                            )}

                                                            {selectedEquipment?.length > 0 && (
                                                                <>
                                                                    <p className="price-text-tires ps-0">
                                                                        {t("equipment")}
                                                                    </p>
                                                                    {selectedEquipment?.map((item, index) => (
                                                                        <p
                                                                            key={index}
                                                                            className="price-text-tires-description ps-0"
                                                                        >
                                                                            ({index + 1}) {item.name}
                                                                        </p>
                                                                    ))}
                                                                </>
                                                            )}

                                                            {selectedAccessories?.length > 0 && (
                                                                <>
                                                                    <p className="price-text-tires ps-0">
                                                                        {selectedAccessories.length === 1
                                                                            ? t("accessory")
                                                                            : t("accessories")}
                                                                    </p>
                                                                    {selectedAccessories?.map((item, index) => (
                                                                        <p
                                                                            key={index}
                                                                            className="price-text-tires-description ps-0"
                                                                        >
                                                                            ({index + 1}) {item.name}
                                                                        </p>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {" "}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 mt-4 mt-lg-0">
                                <div className="total-price-box-tires">
                                    <TractorSidebar/>
                                </div>

                                <div className="d-flex justify-content-between mt-3">
                                    <button
                                        className="btn btn-outline-theme tractor-custom-button-details"
                                        onClick={handleBack}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        {t("back")}
                                    </button>
                                    <button
                                        className="btn btn-danger tractor-custom-button-details tractor-btn-right"
                                        onClick={handleNext}
                                    >
                                        {t("Submit")}
                                        <i className="bi bi-arrow-right"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default TractorReview;
